/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { find } from "lodash";

import { CommandBar, ICommandBarItemProps, Label, Stack, StackItem, Text } from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import {
  bodyContainerStackStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { GrantAnnualExceptionSidePanel } from "Components/GrantAnnualExceptionSidePanel/GrantAnnualExceptionSidePanel";
import { SetProfileToPrivateSidePanel } from "Components/SetProfileToPrivateSidePanel/SetProfileToPrivateSidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { namesLocalized } from "Constants/ProfileConstants";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import {
  fetchUserProfile,
  userProfileAnnualExceptionUpdate,
  userProfileIsPrivateUpdate,
} from "Dataflows/MVP/MVPAction";
import {
  getSelectedUserProfile,
  getUserProfileUpdateAnnualExceptionError,
  getUserProfileUpdateAnnualExceptionOperationStatus,
  getUserProfileUpdateIsPrivateError,
  getUserProfileUpdateIsPrivateOperationStatus,
} from "Dataflows/MVP/UserProfile/MVPUserProfileSelector";
import { formatDate } from "Utility/FormatHelper";

import {
  bodyColumnStackItemStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  commandBarStyles,
  commandBarUnblockStyles,
  labelStyles,
  subHeaderTextStyles,
} from "./MostValuableProfessionalProfile.styles";

/**
 * The Most Valuable Professional Profile component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Profile Details component
 */
const MostValuableProfessionalProfile = (): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles, hasRole } = useAuth();
  const [isSetToPrivatePanelOpen, setIsSetToPrivatePanelOpen] = useState(false);
  const [isGrantAnnualExceptionPanelOpen, setIsGrantAnnualExceptionPanelOpen] = useState(false);
  const profileInfo = useSelector(getSelectedUserProfile);
  const userProfileIsPrivateUpdateStatus = useSelector(
    getUserProfileUpdateIsPrivateOperationStatus
  );
  const userProfileIsPrivateUpdateError = useSelector(getUserProfileUpdateIsPrivateError);

  const userProfileAnnualExceptionUpdateStatus = useSelector(
    getUserProfileUpdateAnnualExceptionOperationStatus
  );
  const userProfileAnnualExceptionUpdateError = useSelector(
    getUserProfileUpdateAnnualExceptionError
  );

  const dispatch = useDispatch();

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  useEffect(() => {
    if (userProfileIsPrivateUpdateStatus === OperationStatus.Succeeded && profileInfo?.id) {
      setIsSetToPrivatePanelOpen(false);
      dispatch(fetchUserProfile(profileInfo.id));
    }
  }, [userProfileIsPrivateUpdateStatus]);

  useEffect(() => {
    if (userProfileAnnualExceptionUpdateStatus === OperationStatus.Succeeded && profileInfo?.id) {
      setIsGrantAnnualExceptionPanelOpen(false);
      dispatch(fetchUserProfile(profileInfo.id));
    }
  }, [userProfileAnnualExceptionUpdateStatus]);

  const handleSetProfileToPrivateTogglePanel = (): void => {
    if (profileInfo && profileInfo.id) {
      setIsSetToPrivatePanelOpen(!isSetToPrivatePanelOpen);
    }
  };

  const handleGrantAnnualExceptionTogglePanel = (): void => {
    if (profileInfo && profileInfo.id) {
      setIsGrantAnnualExceptionPanelOpen(!isGrantAnnualExceptionPanelOpen);
    }
  };

  const handleSetProfileToPrivateSave = (): void => {
    if (profileInfo && profileInfo.id) {
      dispatch(userProfileIsPrivateUpdate(!profileInfo.isPrivate, profileInfo.id));
    }
  };

  const handleGrantAnnualExceptionSave = (justification): void => {
    if (profileInfo && profileInfo.id) {
      dispatch(userProfileAnnualExceptionUpdate(justification, profileInfo.id));
    }
  };

  const canEditUser =
    hasRole(RolesEnum.MVP_BENEFITS_MANAGER) || hasRole(RolesEnum.MVP_PROGRAM_OWNER);
  const canGrantAnnualException =
    (hasRole(RolesEnum.MVP_BUSINESS_GROUP) || hasRole(RolesEnum.MVP_PROGRAM_OWNER)) &&
    profileInfo?.isInCurrentCohort;

  const commandBarItems: ICommandBarItemProps[] = [];
  if (canEditUser) {
    commandBarItems.push({
      key: "setProfileToPrivate",
      text: `Set Profile to ${profileInfo?.isPrivate ? "Public" : "Private"}`,
      iconProps: { iconName: `${profileInfo?.isPrivate ? "UserFollowed" : "UserRemove"}` },
      onClick: (): void => {
        handleSetProfileToPrivateTogglePanel();
      },
    });
  }

  if (canGrantAnnualException) {
    commandBarItems.push({
      key: "grantAnnualException",
      text: "Grant Annual Exception",
      disabled: profileInfo?.isAnnualException,
      onClick: (): void => {
        handleGrantAnnualExceptionTogglePanel();
      },
    });
  }

  return (
    <>
      <Stack tokens={{ childrenGap: "1.5rem" }}>
        {(canEditUser || canGrantAnnualException) && (
          <CommandBar
            items={commandBarItems}
            ariaLabel="Edit user actions"
            styles={profileInfo?.isShippingBlocked ? commandBarUnblockStyles : commandBarStyles}
          />
        )}

        <ContainerWrapper>
          <Stack styles={headerStackStyles}>
            <Text styles={headerTextStyles}>Personal Information</Text>
          </Stack>
          <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>First Name</Label>
                <Text>{profileInfo?.firstName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Last Name</Label>
                <Text>{profileInfo?.lastName}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Localized First Name</Label>
                <Text>{profileInfo?.localizedFirstName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Localized Last Name</Label>
                <Text>{profileInfo?.localizedLastName}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Print Name</Label>
                <Text>
                  {find(namesLocalized, (o) => o.key === !!profileInfo?.awardNameLocalized)?.text}
                </Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Display Name</Label>
                <Text>
                  {find(namesLocalized, (o) => o.key === !!profileInfo?.screenNameLocalized)?.text}
                </Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Title</Label>
                <Text>{profileInfo?.titleName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Date of birth</Label>
                {!!profileInfo?.dateOfBirth ? (
                  <Text>{formatDate(new Date(profileInfo?.dateOfBirth))}</Text>
                ) : (
                  <></>
                )}
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Gender</Label>
                <Text>{profileInfo?.genderName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Pronoun</Label>
                <Text>{profileInfo?.genderPronounPreferenceName}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Shirt Fit Type</Label>
                <Text>{profileInfo?.shirtFitType}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Shirt Size</Label>
                <Text>{profileInfo?.shirtSize}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Languages</Label>
                <Text>{profileInfo?.languages?.join(", ")}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Current Employer/Organization</Label>
                <Text>{profileInfo?.currentEmployerName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Job Title</Label>
                <Text>{profileInfo?.jobTitle}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Company Type</Label>
                <Text>{profileInfo?.companyTypeName}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Preferred Programming Language</Label>
                <Text>{profileInfo?.userProfileProgramLanguage?.join(", ")}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Functional Role</Label>
                <Text>{profileInfo?.userProfileFunctionalRole?.join(", ")}</Text>
              </StackItem>
            </Stack>
          </Stack>
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack styles={headerStackStyles}>
            <Text styles={headerTextStyles}>Biography</Text>
          </Stack>
          <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Headline</Label>
                <Text>{profileInfo?.headline}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Biography</Label>
                <Text>{profileInfo?.biography}</Text>
              </StackItem>
            </Stack>
          </Stack>
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack styles={headerStackStyles}>
            <Text styles={headerTextStyles}>Address</Text>
          </Stack>
          <Stack>
            <Text styles={subHeaderTextStyles}>Primary Address</Text>
          </Stack>
          <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Country Code</Label>
                <Text>{profileInfo?.primaryPhoneCountryOrRegionName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Phone number</Label>
                <Text>{profileInfo?.primaryPhone}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Address Line 1</Label>
                <Text>{profileInfo?.address1}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Address Line 2</Label>
                <Text>{profileInfo?.address2}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>City</Label>
                <Text>{profileInfo?.addressCity}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>State/Province</Label>
                <Text>{profileInfo?.addressStateOrProvince}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Post code</Label>
                <Text>{profileInfo?.addressPostCode}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Country Region</Label>
                <Text>{profileInfo?.addressCountryOrRegionName}</Text>
              </StackItem>
            </Stack>
          </Stack>
          {!profileInfo?.shippingSameAsAddress ? (
            <>
              <Stack>
                <Text styles={subHeaderTextStyles}>Shipping Address</Text>
              </Stack>
              <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
                <Stack horizontal styles={bodyRowStackStyles}>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Country Code</Label>
                    <Text>{profileInfo?.shippingPhoneCountryOrRegionName}</Text>
                  </StackItem>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Phone number</Label>
                    <Text>{profileInfo?.shippingPhoneNumber}</Text>
                  </StackItem>
                </Stack>
                <Stack horizontal styles={bodyRowStackStyles}>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Address Line 1</Label>
                    <Text>{profileInfo?.shippingAddress1}</Text>
                  </StackItem>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Address Line 2</Label>
                    <Text>{profileInfo?.shippingAddress2}</Text>
                  </StackItem>
                </Stack>
                <Stack horizontal styles={bodyRowStackStyles}>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>City</Label>
                    <Text>{profileInfo?.shippingCity}</Text>
                  </StackItem>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>State/Province</Label>
                    <Text>{profileInfo?.shippingStateOrProvince}</Text>
                  </StackItem>
                </Stack>

                <Stack horizontal styles={bodyRowStackStyles}>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Post code</Label>
                    <Text>{profileInfo?.shippingPostCode}</Text>
                  </StackItem>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Country Region</Label>
                    <Text>{profileInfo?.shippingCountryOrRegionName}</Text>
                  </StackItem>
                </Stack>
              </Stack>
            </>
          ) : (
            <></>
          )}
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack styles={headerStackStyles}>
            <Text styles={headerTextStyles}>Social Media</Text>
          </Stack>
          <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
            {profileInfo?.userProfileSocialNetwork.map((x) => (
              <Stack key={x.id} horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Network</Label>
                  <Text>{x?.socialNetworkName}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Link/URL</Label>
                  <Text>{x?.socialNetworkImageLink}</Text>
                </StackItem>
              </Stack>
            ))}
          </Stack>
        </ContainerWrapper>
      </Stack>
      <SetProfileToPrivateSidePanel
        userProfileId={profileInfo?.id ?? 0}
        isPrivate={profileInfo?.isPrivate ?? false}
        isOpen={isSetToPrivatePanelOpen}
        updateStatus={userProfileIsPrivateUpdateStatus}
        updateError={userProfileIsPrivateUpdateError}
        onDismiss={handleSetProfileToPrivateTogglePanel}
        onSave={handleSetProfileToPrivateSave}
      />
      <GrantAnnualExceptionSidePanel
        userProfileId={profileInfo?.id ?? 0}
        isOpen={isGrantAnnualExceptionPanelOpen}
        updateStatus={userProfileAnnualExceptionUpdateStatus}
        updateError={userProfileAnnualExceptionUpdateError}
        onDismiss={handleGrantAnnualExceptionTogglePanel}
        onSave={handleGrantAnnualExceptionSave}
      />
    </>
  );
};

export default MostValuableProfessionalProfile;
